import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, AttemptBadge, ExportedBadge, AuditedBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Play, RotateCcw, FileDown, ArrowRight } from "lucide-react";

const diffColor = {
  medio: "border-amber-300 bg-amber-50 text-amber-700",
  difícil: "border-red-300 bg-red-50 text-red-700",
  fácil: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

export default function Student() {
  const [exercises, setExercises] = useState([]);
  const [sessions, setSessions] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const [ex, se] = await Promise.all([api.get("/exercises"), api.get("/sessions/mine")]);
    setExercises(ex.data);
    setSessions(se.data);
  };

  useEffect(() => { load(); }, []);

  const start = async (exerciseId) => {
    try {
      const { data } = await api.post(`/sessions?exercise_id=${exerciseId}`);
      navigate(`/alumno/sesion/${data.id}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async (s) => {
    try {
      const res = await api.get(`/sessions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${s.public_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const attemptsUsed = (exId) => sessions.filter((s) => s.exercise_id === exId).length;

  return (
    <AppShell title="Panel del alumno">
      <section className="sepp-fade">
        <h2 className="font-display text-lg font-semibold text-foreground">Ejercicios disponibles</h2>
        <p className="mb-4 text-sm text-muted-foreground">Elige un caso para iniciar una entrevista simulada.</p>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {exercises.map((ex) => {
            const used = attemptsUsed(ex.id);
            const maxed = used >= ex.max_attempts;
            return (
              <div key={ex.id} data-testid="exercise-card-item" className="flex flex-col rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
                <div className="flex items-center gap-3">
                  <Avatar className="h-11 w-11">
                    <AvatarImage src={ex.patient_avatar} alt={ex.patient_name} />
                    <AvatarFallback>{ex.patient_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-display text-sm font-semibold leading-tight">{ex.title}</p>
                    <p className="text-xs text-muted-foreground">Paciente: {ex.patient_name}</p>
                  </div>
                </div>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{ex.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={diffColor[ex.difficulty] || diffColor.medio}>{ex.difficulty}</Badge>
                  <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-600">
                    {used}/{ex.max_attempts} intentos
                  </Badge>
                </div>
                <Button className="mt-4" disabled={maxed} onClick={() => start(ex.id)} data-testid="start-session-button">
                  <Play className="mr-2 h-4 w-4" />{maxed ? "Sin intentos" : "Iniciar sesión"}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-lg font-semibold text-foreground">Mis sesiones</h2>
        <p className="mb-4 text-sm text-muted-foreground">Retoma sesiones en progreso o descarga tus transcripciones.</p>
        {sessions.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-white p-8 text-center text-sm text-muted-foreground">
            Aún no has iniciado ninguna sesión.
          </p>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-primary">{s.public_id}</span>
                    <StatusBadge status={s.status} />
                  </div>
                  <p className="mt-1 text-sm font-medium">{s.exercise_title}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <AttemptBadge n={s.attempt_number} />
                    <ExportedBadge exported={s.exported} />
                    <AuditedBadge audited={s.audited} />
                  </div>
                </div>
                <div className="flex gap-2">
                  {s.status === "in_progress" ? (
                    <Button onClick={() => navigate(`/alumno/sesion/${s.id}`)} data-testid="resume-session-button">
                      <RotateCcw className="mr-2 h-4 w-4" />Retomar
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => navigate(`/alumno/sesion/${s.id}`)}>
                        Ver <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <Button variant="outline" onClick={() => downloadPdf(s)} data-testid="chat-export-pdf-button">
                        <FileDown className="mr-2 h-4 w-4" />PDF
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </AppShell>
  );
}
